import {
  CesiumTerrainProvider,
  Credit,
  EllipsoidTerrainProvider,
  Event as CesiumEvent,
  HeightmapTerrainData,
  Rectangle,
  Resource,
  TerrainProvider,
  TileAvailability,
  WebMercatorTilingScheme,
  createWorldTerrainAsync,
  type Request,
  type TerrainData,
} from 'cesium';
import * as Lerc from 'lerc';
import lercWasmUrl from 'lerc/lerc-wasm.wasm?url';
import { config } from '../app/config';

export type TerrainPath = 'self-hosted' | 'arcgis' | 'ion' | 'ellipsoid';

export interface ResolvedTerrain {
  provider: TerrainProvider;
  path: TerrainPath;
}

/**
 * Queensland DEM terrain from the public QldDem ImageServer LERC tile cache
 * (resolves Spike S2 — no token needed).
 *
 * Cesium's ArcGISTiledElevationTerrainProvider cannot consume this service: the
 * cache numbers tiles in the GLOBAL web-mercator grid (tileInfo.origin is
 * -20037508,20037508) while its advertised extent is Queensland-only, and the
 * Cesium provider derives tile indices from the extent — so every tile URL it
 * builds 404s. This provider talks to the cache directly on a global
 * WebMercatorTilingScheme and decodes LERC with Esri's decoder.
 *
 * The cache only has tiles over Queensland at levels 5–18 (probed live; levels
 * 0–4 return 404, which would stall Cesium's quadtree at the root). Synthetic
 * flat tiles are served below the cache and outside its footprint, and
 * availability is advertised up to a level present statewide (SRTM-fill areas
 * included) so sampleTerrainMostDetailed — used by the track builder and the
 * measurement tools — never lands on a missing tile and silently reads 0.
 */
const QLD_MIN_CACHED_LEVEL = 5;
const QLD_MAX_SAMPLED_LEVEL = 16; // ~2.1 m ground resolution at SEQ latitude
const QLD_RECTANGLE = Rectangle.fromDegrees(137.4, -29.5, 154.5, -8.9);
const TILE_SIZE = 257; // ArcGIS elevation tiles overlap edges: 257×257 samples

// A fresh instance per request: Cesium's mesh-creation worker takes ownership
// of (transfers) the height buffer, so instances cannot be shared across tiles.
function flatTile(): HeightmapTerrainData {
  return new HeightmapTerrainData({
    buffer: new Float32Array(16 * 16),
    width: 16,
    height: 16,
  });
}

class QldDemTerrainProvider implements TerrainProvider {
  readonly tilingScheme = new WebMercatorTilingScheme();
  readonly availability: TileAvailability;
  readonly errorEvent = new CesiumEvent();
  readonly credit = new Credit('© State of Queensland (Department of Resources)');
  readonly hasWaterMask = false;
  readonly hasVertexNormals = false;
  private readonly resource: Resource;
  private readonly levelZeroError: number;

  constructor(demImageServerUrl: string) {
    // Trailing slash matters: without it, relative resolution of `tile/...`
    // replaces the final path segment instead of appending to it.
    this.resource = new Resource({
      url: demImageServerUrl.endsWith('/') ? demImageServerUrl : `${demImageServerUrl}/`,
    });
    this.levelZeroError = TerrainProvider.getEstimatedLevelZeroGeometricErrorForAHeightmap(
      this.tilingScheme.ellipsoid,
      TILE_SIZE,
      this.tilingScheme.getNumberOfXTilesAtLevel(0),
    );

    this.availability = new TileAvailability(this.tilingScheme, QLD_MAX_SAMPLED_LEVEL);
    const cached =
      Rectangle.intersection(QLD_RECTANGLE, this.tilingScheme.rectangle) ?? QLD_RECTANGLE;
    for (let level = 0; level <= QLD_MAX_SAMPLED_LEVEL; level++) {
      if (level < QLD_MIN_CACHED_LEVEL) {
        // Whole world: synthetic flat tiles below the cache.
        this.availability.addAvailableTileRange(
          level,
          0,
          0,
          this.tilingScheme.getNumberOfXTilesAtLevel(level) - 1,
          this.tilingScheme.getNumberOfYTilesAtLevel(level) - 1,
        );
        continue;
      }
      // Tile x grows eastward (min at SW corner); tile y grows southward (min at NE).
      const sw = this.tilingScheme.positionToTileXY(Rectangle.southwest(cached), level);
      const ne = this.tilingScheme.positionToTileXY(Rectangle.northeast(cached), level);
      if (sw && ne) this.availability.addAvailableTileRange(level, sw.x, ne.y, ne.x, sw.y);
    }
  }

  getLevelMaximumGeometricError(level: number): number {
    return this.levelZeroError / (1 << level);
  }

  getTileDataAvailable(x: number, y: number, level: number): boolean {
    return this.availability.isTileAvailable(level, x, y);
  }

  loadTileDataAvailability(): undefined {
    return undefined;
  }

  requestTileGeometry(
    x: number,
    y: number,
    level: number,
    request?: Request,
  ): Promise<TerrainData> | undefined {
    if (level < QLD_MIN_CACHED_LEVEL) return Promise.resolve(flatTile());

    const tile = this.resource.getDerivedResource({
      url: `tile/${level}/${y}/${x}`,
      request,
    });
    const promise = tile.fetchArrayBuffer();
    if (!promise) return undefined; // throttled — Cesium will retry
    return promise.then(
      (buffer) => {
        const decoded = Lerc.decode(buffer as ArrayBuffer);
        const heights = decoded.pixels[0] as Float32Array;
        if (decoded.mask) {
          for (let i = 0; i < heights.length; i++) if (!decoded.mask[i]) heights[i] = 0;
        }
        return new HeightmapTerrainData({
          buffer: heights,
          width: decoded.width,
          height: decoded.height,
        });
      },
      () => flatTile(), // missing tile inside the padded rectangle
    );
  }
}

/**
 * Resolve a terrain provider, preferring the most accurate available source.
 *
 *  1. self-hosted quantized-mesh tiles (path B) if config.terrain.tilesUrl is set
 *  2. Cesium Ion world terrain — optional override, only attempted when
 *     VITE_CESIUM_ION_TOKEN is configured
 *  3. the Queensland QldDem LERC tile cache (path A) — the default working
 *     source; public, no token
 *  4. a smooth ellipsoid (last resort) so the app always runs
 */
export async function resolveTerrain(): Promise<ResolvedTerrain> {
  if (config.terrain.tilesUrl) {
    try {
      const provider = await CesiumTerrainProvider.fromUrl(config.terrain.tilesUrl, {
        requestVertexNormals: true,
      });
      return { provider, path: 'self-hosted' };
    } catch (err) {
      console.warn('[terrain] self-hosted tiles failed', err);
    }
  }

  if (config.ion.token) {
    try {
      const provider = await createWorldTerrainAsync();
      return { provider, path: 'ion' };
    } catch (err) {
      console.warn('[terrain] Ion world terrain failed', err);
    }
  }

  try {
    // Confirm the service is alive before committing to it, and load the LERC
    // wasm decoder once up front.
    const meta = (await new Resource({
      url: config.terrain.demImageServer,
      queryParameters: { f: 'json' },
    }).fetchJson()) as { error?: unknown; tileInfo?: unknown };
    if (meta.error || !meta.tileInfo) throw new Error('QldDem metadata unavailable');
    await Lerc.load({ locateFile: () => lercWasmUrl });
    return { provider: new QldDemTerrainProvider(config.terrain.demImageServer), path: 'arcgis' };
  } catch (err) {
    // Fall through to the ellipsoid so the app still runs.
    console.info('[terrain] QLD DEM unavailable; using ellipsoid', err);
  }

  return { provider: new EllipsoidTerrainProvider(), path: 'ellipsoid' };
}
